/**
 * Function Reconcile Dialog
 *
 * Surfaces every linked F&O Job whose JobFamilyId (Function) differs from
 * the linked Role Master entry's functional_area, with per-row remediation
 * in either direction:
 *   • Push RM → F&O   — overwrite the Job's family with the Role's function
 *                       (via /push-role-families-to-jobs, force + job_ids)
 *   • Accept F&O → RM — set the Role's functional_area to the Job's family
 *                       (via /accept-fo-function)
 *
 * The server normalizes the comparison ('SALES-AND-MARKETING' ≈
 * 'Sales and Marketing' ≈ 'Sales & Marketing'), so listed rows are genuine
 * discrepancies, not formatting noise.
 */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Loader2, ArrowRight, ArrowLeft, RefreshCw, CheckCircle2, GitCompareArrows,
} from 'lucide-react';
import { useConfirm } from '@/hooks/useConfirm';
import {
  jobFamilySyncService,
  FunctionSyncIssue,
  FunctionSyncIssuesResponse,
} from '@/services/api/organizationService';

const STATUS_META: Record<FunctionSyncIssue['status'], { label: string; className: string }> = {
  mismatch: { label: 'Mismatch', className: 'bg-red-50 text-red-700 border-red-200' },
  fo_family_missing: { label: 'No family in F&O', className: 'bg-amber-50 text-amber-700 border-amber-200' },
  rm_function_missing: { label: 'No function on Role', className: 'bg-blue-50 text-blue-700 border-blue-200' },
  job_detail_corrupted: { label: 'Job corrupted in F&O', className: 'bg-purple-50 text-purple-700 border-purple-200' },
};

interface FunctionReconcileDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Called after any successful remediation so the parent can refresh the tree. */
  onMutated?: () => void;
}

const FunctionReconcileDialog: React.FC<FunctionReconcileDialogProps> = ({
  open, onOpenChange, onMutated,
}) => {
  const { confirm: showConfirm } = useConfirm();
  const [data, setData] = useState<FunctionSyncIssuesResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [acting, setActing] = useState<'push' | 'accept' | null>(null);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [statusFilter, setStatusFilter] = useState<FunctionSyncIssue['status'] | 'all'>('all');
  const [resultMessage, setResultMessage] = useState<string | null>(null);

  const loadIssues = useCallback(async () => {
    setLoading(true);
    setResultMessage(null);
    try {
      const result = await jobFamilySyncService.getFunctionSyncIssues();
      setData(result);
      // Drop selections that no longer exist after a refresh
      setSelected(prev => {
        const valid = new Set(result.issues.map(i => i.entry_id));
        return new Set(Array.from(prev).filter(id => valid.has(id)));
      });
    } catch (error) {
      console.error('Failed to load function sync issues:', error);
      setResultMessage('Failed to load issues — check integration-service connectivity.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) loadIssues();
    else {
      setSelected(new Set());
      setStatusFilter('all');
      setResultMessage(null);
    }
  }, [open, loadIssues]);

  const issues = data?.issues ?? [];
  const filteredIssues = useMemo(
    () => statusFilter === 'all' ? issues : issues.filter(i => i.status === statusFilter),
    [issues, statusFilter],
  );

  const selectedIssues = useMemo(
    () => issues.filter(i => selected.has(i.entry_id)),
    [issues, selected],
  );

  // Push needs a Role function to push; Accept needs an F&O family to accept.
  // Jobs with corrupted date-effective detail rows are excluded from BOTH —
  // no OData write can land on them until they're repaired in the F&O UI
  // (open the Job, fill the fields, Save — the form rebuilds the detail row).
  const pushable = selectedIssues.filter(i => i.rm_function && i.status !== 'job_detail_corrupted');
  const acceptable = selectedIssues.filter(i => i.fo_family_id && i.status !== 'job_detail_corrupted');

  const toggleRow = (entryId: number, checked: boolean) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (checked) next.add(entryId);
      else next.delete(entryId);
      return next;
    });
  };

  const allFilteredSelected = filteredIssues.length > 0 &&
    filteredIssues.every(i => selected.has(i.entry_id));

  const toggleAllFiltered = (checked: boolean) => {
    setSelected(prev => {
      const next = new Set(prev);
      filteredIssues.forEach(i => {
        if (checked) next.add(i.entry_id);
        else next.delete(i.entry_id);
      });
      return next;
    });
  };

  const handlePushToFO = async () => {
    if (pushable.length === 0) return;
    const confirmed = await showConfirm(
      `Push ${pushable.length} Role function${pushable.length === 1 ? '' : 's'} to F&O? ` +
      `This OVERWRITES each Job's family with the Role's function and cascades to PositionDetails.`,
      'Push RM → F&O',
      { confirmText: `Push ${pushable.length}`, type: 'warning' },
    );
    if (!confirmed) return;
    setActing('push');
    setResultMessage(null);
    try {
      const result = await jobFamilySyncService.pushRoleFamiliesToJobs({
        dry_run: false,
        force: true,
        job_ids: pushable.map(i => i.fo_job_id),
      });
      setResultMessage(
        `Pushed ${result.succeeded ?? 0} Job famil${(result.succeeded ?? 0) === 1 ? 'y' : 'ies'}` +
        (result.failed ? `, ${result.failed} failed` : '') + '.',
      );
      onMutated?.();
      await loadIssues();
    } catch (error: any) {
      setResultMessage(error?.response?.data?.detail || 'Push failed');
    } finally {
      setActing(null);
    }
  };

  const handleAcceptFromFO = async () => {
    if (acceptable.length === 0) return;
    const confirmed = await showConfirm(
      `Accept F&O for ${acceptable.length} Role${acceptable.length === 1 ? '' : 's'}? ` +
      `This sets each Role's function to its linked Job's family in F&O.`,
      'Accept F&O → RM',
      { confirmText: `Accept ${acceptable.length}`, type: 'warning' },
    );
    if (!confirmed) return;
    setActing('accept');
    setResultMessage(null);
    try {
      const result = await jobFamilySyncService.acceptFoFunctions(
        acceptable.map(i => i.entry_id),
      );
      setResultMessage(
        `Updated ${result.updated} Role${result.updated === 1 ? '' : 's'}` +
        (result.skipped ? ` (${result.skipped} skipped)` : '') + '.',
      );
      onMutated?.();
      await loadIssues();
    } catch (error: any) {
      setResultMessage(error?.response?.data?.detail || 'Accept failed');
    } finally {
      setActing(null);
    }
  };

  const counts = data ? [
    { key: 'mismatch' as const, count: data.mismatch_count },
    { key: 'fo_family_missing' as const, count: data.fo_family_missing_count },
    { key: 'rm_function_missing' as const, count: data.rm_function_missing_count },
    { key: 'job_detail_corrupted' as const, count: data.job_detail_corrupted_count ?? 0 },
  ].filter(c => c.count > 0) : [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <GitCompareArrows className="h-5 w-5 text-blue-600" />
            Reconcile Functions — Jobs vs Roles
          </DialogTitle>
          <DialogDescription>
            Linked F&amp;O Jobs whose family differs from the linked Role&apos;s function.
            Select rows, then push the Role&apos;s function to F&amp;O or accept the F&amp;O family onto the Role.
          </DialogDescription>
        </DialogHeader>

        {/* Summary chips + refresh */}
        <div className="flex items-center gap-2 flex-wrap">
          {data && (
            <span className="text-xs text-gray-500 mr-1">
              {data.in_sync} of {data.total_linked} linked Jobs in sync
              {data.job_not_found_count > 0 && (
                <span className="text-gray-400"> · {data.job_not_found_count} linked Jobs missing in F&amp;O (see Reconciliation tab)</span>
              )}
            </span>
          )}
          <div className="ml-auto flex items-center gap-1.5">
            {counts.map(({ key, count }) => (
              <Badge
                key={key}
                variant="outline"
                className={`cursor-pointer select-none text-xs ${STATUS_META[key].className} ${statusFilter === key ? 'ring-1 ring-offset-1 ring-gray-400' : ''}`}
                onClick={() => setStatusFilter(statusFilter === key ? 'all' : key)}
              >
                {count} {STATUS_META[key].label}
              </Badge>
            ))}
            <Button size="sm" variant="ghost" className="h-7 px-2" onClick={loadIssues} disabled={loading}>
              <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>

        {/* Issues table */}
        <div className="flex-1 min-h-[260px] overflow-y-auto border rounded-md">
          {loading ? (
            <div className="py-16 text-center text-gray-400">
              <Loader2 className="h-5 w-5 animate-spin inline mr-2" />
              Comparing Jobs against linked Roles...
            </div>
          ) : filteredIssues.length === 0 ? (
            <div className="py-16 text-center text-gray-400 text-sm">
              <CheckCircle2 className="h-8 w-8 mx-auto mb-2 text-emerald-400" />
              {issues.length === 0
                ? 'All linked Jobs match their Role functions. Nothing to reconcile.'
                : 'No issues match the current filter.'}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="text-xs">
                  <TableHead className="w-8">
                    <Checkbox
                      checked={allFilteredSelected}
                      onCheckedChange={(c) => toggleAllFiltered(c === true)}
                    />
                  </TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>RM Function</TableHead>
                  <TableHead className="w-8" />
                  <TableHead>F&amp;O Job Family</TableHead>
                  <TableHead>F&amp;O Job</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredIssues.map(issue => (
                  <TableRow key={issue.entry_id} className="text-xs">
                    <TableCell>
                      <Checkbox
                        checked={selected.has(issue.entry_id)}
                        onCheckedChange={(c) => toggleRow(issue.entry_id, c === true)}
                      />
                    </TableCell>
                    <TableCell className="max-w-[220px]">
                      <div className="font-medium truncate" title={issue.title}>
                        {issue.title}
                        {issue.rm_status && issue.rm_status !== 'active' && (
                          <Badge variant="outline" className="ml-1.5 text-[9px] px-1 py-0 text-gray-500 border-gray-300 align-middle">
                            {issue.rm_status}
                          </Badge>
                        )}
                      </div>
                      <div className="font-mono text-[10px] text-gray-400">{issue.role_code}</div>
                    </TableCell>
                    <TableCell>
                      {issue.rm_function
                        ? <span className="font-medium">{issue.rm_function}</span>
                        : <span className="text-gray-300 italic">none</span>}
                    </TableCell>
                    <TableCell className="text-gray-300">≠</TableCell>
                    <TableCell>
                      {issue.fo_family_id ? (
                        <div>
                          <span className="font-medium">{issue.fo_family_description || issue.fo_family_id}</span>
                          {issue.fo_family_description && issue.fo_family_description !== issue.fo_family_id && (
                            <span className="ml-1 font-mono text-[10px] text-gray-400">{issue.fo_family_id}</span>
                          )}
                        </div>
                      ) : (
                        <span className="text-gray-300 italic">none</span>
                      )}
                    </TableCell>
                    <TableCell className="max-w-[180px]">
                      <span className="font-mono text-[10px] text-gray-500 truncate block" title={issue.fo_job_id}>
                        {issue.fo_job_id}
                      </span>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className={`text-[10px] ${STATUS_META[issue.status].className}`}>
                        {STATUS_META[issue.status].label}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>

        {resultMessage && (
          <p className="text-xs text-gray-600">{resultMessage}</p>
        )}
        {(data?.job_detail_corrupted_count ?? 0) > 0 && (
          <p className="text-xs text-purple-700">
            &quot;Job corrupted in F&amp;O&quot; rows have broken date-effective detail records that no
            sync can write to. Repair in F&amp;O: Human Resources &gt; Jobs &gt; open the Job &gt; enter the
            missing details &gt; Save — then Refresh here.
          </p>
        )}

        <DialogFooter className="gap-2">
          <span className="text-xs text-gray-400 mr-auto self-center">
            {selected.size} selected
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={pushable.length === 0 || acting !== null}
            onClick={handlePushToFO}
            title="Overwrite each selected Job's family in F&O with the Role's function"
          >
            {acting === 'push'
              ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
              : <ArrowRight className="h-3.5 w-3.5 mr-1" />}
            Push RM → F&amp;O{pushable.length > 0 ? ` (${pushable.length})` : ''}
          </Button>
          <Button
            variant="outline"
            size="sm"
            disabled={acceptable.length === 0 || acting !== null}
            onClick={handleAcceptFromFO}
            title="Set each selected Role's function to its Job's family in F&O"
          >
            {acting === 'accept'
              ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
              : <ArrowLeft className="h-3.5 w-3.5 mr-1" />}
            Accept F&amp;O → RM{acceptable.length > 0 ? ` (${acceptable.length})` : ''}
          </Button>
          <Button size="sm" onClick={() => onOpenChange(false)}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default FunctionReconcileDialog;
